import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faTint,
  faChevronLeft,
  faChevronRight,
  faExclamationTriangle,
  faRedo,
  faPlus,
  faEye,
  faProjectDiagram,
  faTimesCircle,
  faClock,
  faCircleXmark,
  faCircleCheck,
  faFileContract,
} from '@fortawesome/free-solid-svg-icons';
import { fuelEnquiryService } from '../../services/fuelEnquiryService';
import { useFlashMessage } from '../FlashMessage';
import StageAdvanceModal from './StageAdvanceModal';
import InvoiceRelationshipModal from '../Invoicing/InvoiceRelationshipModal';
import PdfPreviewModal from '../shared/PdfPreviewModal';

// Mirrors ``generateGroupColor`` in ``TradeOperationsLayout.js`` so a
// Fuels user toggling between the Recaps tab and the Enquiries tab sees
// identical group-bar palette + hashing logic. We duplicate (rather than
// import) to keep the helper a private module-level utility, exactly the
// way TradeOperationsLayout treats its own copy.
const generateGroupColor = (groupId) => {
  const colors = [
    ['#4a6b25', '#FFFFFF'],
    ['#5a7b35', '#FFFFFF'],
    ['#3a5b15', '#FFFFFF'],
    ['#65a30d', '#FFFFFF'],
    ['#4d7c0f', '#FFFFFF'],
    ['#3f6212', '#FFFFFF'],
    ['#15803d', '#FFFFFF'],
    ['#166534', '#FFFFFF'],
    ['#2f855a', '#FFFFFF'],
    ['#38a169', '#FFFFFF'],
    ['#276749', '#FFFFFF'],
    ['#6b8e23', '#FFFFFF'],
    ['#557a2e', '#FFFFFF'],
    ['#4b6b1f', '#FFFFFF'],
    ['#3d5a1f', '#FFFFFF'],
  ];
  const hash = String(groupId).split('').reduce(
    (acc, char) => char.charCodeAt(0) + ((acc << 5) - acc),
    0,
  );
  const pair = colors[Math.abs(hash) % colors.length];
  return { backgroundColor: pair[0], color: pair[1] };
};

// Stage palette -- bunkers 2-screen redesign. Keys map to
// ``FuelEnquiry.STAGE_*`` values in ``backend/fuel_enquiries/models.py``.
const STAGE_BADGE_CLASS = {
  draft: 'fe-stage-badge fe-stage-new',
  with_supply_trader: 'fe-stage-badge fe-stage-supply',
  recapped: 'fe-stage-badge fe-stage-sent',
  lost: 'fe-stage-badge fe-stage-lost',
};

// The backend ``CREDIT_STATUS_*`` values are the source of truth
// (``FuelEnquiry.CREDIT_STATUS_CHOICES``). ``awaiting_credit_approval``
// was added with the bunkers 2-screen redesign to represent a draft
// paused on a Credit Increase Request.
const CREDIT_BADGE_CLASS = {
  pending: 'fe-credit-badge fe-credit-pending',
  approved: 'fe-credit-badge fe-credit-approved',
  denied: 'fe-credit-badge fe-credit-denied',
  awaiting_credit_approval: 'fe-credit-badge fe-credit-pending',
  n_a: 'fe-credit-badge fe-credit-na',
};

const STAGE_OPTIONS = [
  { value: '', label: 'All Stages' },
  { value: 'draft', label: 'Draft' },
  { value: 'with_supply_trader', label: 'With Supply Trader' },
  { value: 'recapped', label: 'Recapped' },
  { value: 'lost', label: 'Lost' },
];

// Archived enquiries exist and no screen could reach them: on the live system
// ENQ-0063, 0044, 0040 and 0024 were archived, therefore absent from the table,
// therefore indistinguishable from deleted. The server has always accepted
// ``include_archived``; nothing ever sent it.
const ARCHIVE_OPTIONS = [
  { value: 'live', label: 'Live enquiries' },
  { value: 'archived', label: 'Archived only' },
  { value: 'all', label: 'Live + archived' },
];

const CREDIT_OPTIONS = [
  { value: '', label: 'All Credit Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'denied', label: 'Denied' },
  { value: 'awaiting_credit_approval', label: 'Awaiting Credit Approval' },
  { value: 'n_a', label: 'No Additional Credit Needed' },
];


const prettyStage = (raw) => {
  if (!raw) return '';
  return raw
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const formatDateTime = (raw) => {
  if (!raw) return '-';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return '-';
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hh = date.getHours().toString().padStart(2, '0');
  const mm = date.getMinutes().toString().padStart(2, '0');
  return `${day}/${month}/${year} ${hh}:${mm}`;
};

const FuelEnquiryList = ({ commodityType: commodityTypeProp = 'fuels' } = {}) => {
  const commodityType = commodityTypeProp === 'fuels' ? 'fuels' : 'solids';
  const history = useHistory();

  const [filterState, setFilterState] = useState({
    stage: '',
    credit_status: '',
    customer_trader: '',
    supplier_trader: '',
    archived: 'live',
    // Hedging desk (Sept 2026): show only the enquiries whose sales and
    // purchase pricing bases disagree. Off by default -- the list a trader
    // opens must not silently be a subset of their book.
    requires_hedging: false,
  });
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Multi-enquiry grouping state. Mirrors the recap-grouping pattern in
  // ``TradeOperationsLayout.js`` (``recapGroupingMode`` /
  // ``selectedRecaps`` / ``handleGroupRecaps``).
  const [groupingMode, setGroupingMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [groupingBusy, setGroupingBusy] = useState(false);
  const { showSuccess, showError } = useFlashMessage();

  // Per-row Actions dropdown (key === enquiry id of the open dropdown,
  // or null when none is open). Mirrors the ``dropdownVisible`` /
  // ``dropdownRefs`` pattern in TradeOperationsLayout.js (lines ~294,
  // 1472, 2186) so the visual treatment matches the Deals/Recaps gear
  // dropdowns exactly.
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const dropdownRefs = useRef({});

  // "View Relationships" modal state. The modal itself is the same
  // component used by the recap/deal/delivery rows -- we just pass
  // ``documentType="fuel_enquiry"`` so it hits the new backend graph
  // endpoint.
  const [showRelationshipModal, setShowRelationshipModal] = useState(false);
  const [relationshipEnquiry, setRelationshipEnquiry] = useState(null);

  // "Mark as Lost" confirmation modal state. Reuses the existing
  // ``StageAdvanceModal`` (``action="lost"`` + ``requireNotes``).
  const [lostModalEnquiry, setLostModalEnquiry] = useState(null);
  const [lostSubmitting, setLostSubmitting] = useState(false);
  const [lostError, setLostError] = useState(null);

  // Customer Confirmation PDF preview, triggered from the row Actions menu.
  // Only offered once the enquiry is recapped (``row.trade_recap`` set) so it
  // matches the enquiry-form gate. ``url`` is an object URL we revoke on close.
  const [confirmation, setConfirmation] = useState({
    open: false,
    loadingId: null,
    url: null,
    fileName: '',
  });

  const requestIdRef = useRef(0);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // No ``page``: ``FuelEnquiryListPagination`` only paginates when the caller
  // asks for a page, so omitting it returns the WHOLE list — which is what this
  // screen was always meant to show ("render the full quarter at once, like
  // Deals"). Sending ``page=1&page_size=25`` while the Prev/Next controls sat
  // commented out is what made every enquiry past the 25th unreachable: 69 on
  // the system, 25 on the screen, and nothing saying so. Reported 2026-09-03.
  const queryParams = useMemo(() => {
    const params = {
      commodityType,
    };
    if (filterState.stage) params.stage = filterState.stage;
    if (filterState.credit_status) params.credit_status = filterState.credit_status;
    if (filterState.customer_trader) params.customer_trader = filterState.customer_trader;
    if (filterState.supplier_trader) params.supplier_trader = filterState.supplier_trader;
    if (debouncedSearch) params.search = debouncedSearch;
    if (filterState.requires_hedging) params.requires_hedging = 'true';
    // Default stays exactly as it was: live enquiries only. The other two
    // options are the first way anyone has ever been able to reach an
    // archived enquiry from the list.
    if (filterState.archived === 'archived') {
      params.include_archived = 1;
      params.is_archived = true;
    } else if (filterState.archived === 'all') {
      params.include_archived = 1;
    }
    return params;
  }, [commodityType, filterState, debouncedSearch]);

  // Archived rows reach this table two ways now: the Show dropdown, and any
  // search (a search must never hide a matching enquiry -- see the server's
  // get_queryset). Either way the count line says how many.
  const archivedOnScreen = useMemo(
    () => rows.filter((row) => row.is_archived).length,
    [rows],
  );

  const cacheKey = useMemo(
    () => `fuelEnquiries:list:${commodityType}:${JSON.stringify({ ...filterState, search: debouncedSearch })}`,
    [commodityType, filterState, debouncedSearch]
  );

  const fetchList = async () => {
    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await fuelEnquiryService.list(queryParams);
      if (reqId !== requestIdRef.current) return;
      // Two shapes: a flat array (no ``page`` asked for — the normal path
      // here) or DRF's {count, results} envelope. Reading only ``results``
      // rendered an EMPTY table the moment the request stopped asking for a
      // page, which is a worse failure than the truncation it replaced.
      const list = Array.isArray(data) ? data : (Array.isArray(data.results) ? data.results : []);
      setRows(list);
      setCount(typeof data.count === 'number' ? data.count : list.length);
      setHasNext(Boolean(data.next));
      setHasPrev(Boolean(data.previous));
    } catch (err) {
      if (reqId !== requestIdRef.current) return;
      console.error('Error loading fuel enquiries:', err);
      setError(err?.response?.data?.detail || err?.message || 'Unable to load bunker enquiries.');
      setRows([]);
      setCount(0);
      setHasNext(false);
      setHasPrev(false);
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (commodityType !== 'fuels') return;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, commodityType]);


  const handleFilterChange = (field, value) => {
    setFilterState((prev) => ({ ...prev, [field]: value }));
  };

  const handleRowClick = (row) => {
    history.push(`/dashboard/trades/fuels/fuel-enquiries/${row.id}`, {
      commodityType,
    });
  };

  const handleNewEnquiry = () => {
    history.push('/dashboard/trades/fuels/fuel-enquiries/new', {
      commodityType,
    });
  };

  // ---- Per-row Actions dropdown ----
  //
  // Outside-click + Esc handling mirrors TradeOperationsLayout.js
  // (lines ~289-309) so behaviour matches the Deals/Recaps gear menus.
  useEffect(() => {
    if (openDropdownId === null) return undefined;
    const handleClickOutside = (event) => {
      const ref = dropdownRefs.current[openDropdownId];
      if (ref && !ref.contains(event.target)) {
        setOpenDropdownId(null);
      }
    };
    const handleEsc = (event) => {
      if (event.key === 'Escape') setOpenDropdownId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [openDropdownId]);

  const toggleDropdown = (enquiryId) => {
    setOpenDropdownId((current) => (current === enquiryId ? null : enquiryId));
  };

  const handleViewEnquiry = (row) => {
    setOpenDropdownId(null);
    handleRowClick(row);
  };

  const handleViewRelationships = (row) => {
    setOpenDropdownId(null);
    setRelationshipEnquiry(row);
    setShowRelationshipModal(true);
  };

  // Customer Confirmation — generate + preview the PDF straight from the row.
  // Guarded on the linked Sales Recap (``row.trade_recap``); the menu item is
  // disabled until then, but we re-check here as a safety net.
  const handleCustomerConfirmation = async (row) => {
    if (!row?.trade_recap) return;
    setOpenDropdownId(null);
    setConfirmation((prev) => ({ ...prev, loadingId: row.id }));
    try {
      const blob = await fuelEnquiryService.customerConfirmationPdf(row.id);
      const url = URL.createObjectURL(blob);
      const fileName = `Customer Confirmation ${row.display_id || row.id}.pdf`;
      setConfirmation({ open: true, loadingId: null, url, fileName });
    } catch (err) {
      console.error('Customer Confirmation generation failed:', err);
      showError('Could not generate the Customer Confirmation PDF. Please try again.');
      setConfirmation((prev) => ({ ...prev, loadingId: null }));
    }
  };

  const handleCloseConfirmation = () => {
    setConfirmation((prev) => {
      if (prev.url) URL.revokeObjectURL(prev.url);
      return { open: false, loadingId: null, url: null, fileName: '' };
    });
  };

  const handleDownloadConfirmation = () => {
    if (!confirmation.url) return;
    const link = document.createElement('a');
    link.href = confirmation.url;
    link.download = confirmation.fileName || 'Customer Confirmation.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenMarkLost = (row) => {
    setOpenDropdownId(null);
    setLostError(null);
    setLostModalEnquiry(row);
  };

  const handleCloseMarkLost = () => {
    if (lostSubmitting) return;
    setLostModalEnquiry(null);
    setLostError(null);
  };

  const handleConfirmMarkLost = async (notes) => {
    if (!lostModalEnquiry) return;
    setLostSubmitting(true);
    setLostError(null);
    try {
      await fuelEnquiryService.advanceStage(lostModalEnquiry.id, {
        target_stage: 'lost',
        notes,
      });
      showSuccess(`Enquiry ${lostModalEnquiry.display_id || lostModalEnquiry.id} marked as Lost.`);
      setLostModalEnquiry(null);
      fetchList();
    } catch (err) {
      console.error('Mark-as-lost failed:', err);
      const detail = err?.response?.data?.detail
        || err?.response?.data?.error
        || err?.message
        || 'Failed to mark enquiry as Lost.';
      setLostError(detail);
    } finally {
      setLostSubmitting(false);
    }
  };

  // ---- Multi-enquiry grouping ----

  const enterGroupingMode = () => {
    setGroupingMode(true);
    setSelectedIds([]);
  };

  const exitGroupingMode = () => {
    setGroupingMode(false);
    setSelectedIds([]);
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  };

  const handleGroupSelected = async () => {
    if (selectedIds.length < 2) {
      showError('Select at least 2 enquiries to group.');
      return;
    }
    setGroupingBusy(true);
    try {
      const result = await fuelEnquiryService.groupEnquiries(selectedIds);
      showSuccess(
        `Grouped ${result.count} enquiries under ${result.group_id}.`
      );
      exitGroupingMode();
      fetchList();
    } catch (err) {
      console.error('Group failed:', err);
      const detail = err?.response?.data?.error
        || err?.response?.data?.detail
        || err?.message
        || 'Failed to group enquiries.';
      showError(detail);
    } finally {
      setGroupingBusy(false);
    }
  };

  const handleUngroupSelected = async () => {
    if (selectedIds.length === 0) {
      showError('Select at least one enquiry to ungroup.');
      return;
    }
    setGroupingBusy(true);
    try {
      const result = await fuelEnquiryService.ungroupEnquiries(selectedIds);
      showSuccess(
        result.count > 0
          ? `Ungrouped ${result.count} enquiries.`
          : 'Selected enquiries were not part of a group.'
      );
      exitGroupingMode();
      fetchList();
    } catch (err) {
      console.error('Ungroup failed:', err);
      const detail = err?.response?.data?.error
        || err?.response?.data?.detail
        || err?.message
        || 'Failed to ungroup enquiries.';
      showError(detail);
    } finally {
      setGroupingBusy(false);
    }
  };

  // Reorders the flat list so rows sharing the same
  // ``enquiry_grouping_identifier`` sit next to each other (preserving
  // server order within each group), and emits per-row positional flags
  // (``isFirstInGroup`` / ``isLastInGroup`` / ``isFirstInTable`` /
  // ``isLastInTable``) used for the recap-style corner-rounding logic.
  // Mirrors how TradeOperationsLayout builds ``groupedRecaps``.
  const displayRows = useMemo(() => {
    const buckets = new Map();
    const order = [];
    rows.forEach((row) => {
      const key = row.enquiry_grouping_identifier || `single_${row.id}`;
      if (!buckets.has(key)) {
        buckets.set(key, []);
        order.push(key);
      }
      buckets.get(key).push(row);
    });

    const flat = [];
    let tableIndex = 0;
    const totalRows = rows.length;
    order.forEach((key) => {
      const bucket = buckets.get(key);
      bucket.forEach((row, i) => {
        flat.push({
          row,
          groupKey: key,
          groupSize: bucket.length,
          groupId: row.enquiry_grouping_identifier,
          isFirstInGroup: i === 0,
          isLastInGroup: i === bucket.length - 1,
          isFirstInTable: tableIndex === 0,
          isLastInTable: tableIndex === totalRows - 1,
        });
        tableIndex += 1;
      });
    });
    return flat;
  }, [rows]);

  const renderStageBadge = (stage) => {
    const klass = STAGE_BADGE_CLASS[stage] || 'fe-stage-badge fe-stage-default';
    return <span className={klass}>{prettyStage(stage)}</span>;
  };

  // ``condition`` is the deal's unmet "subject to INV-2050 being paid" (ClickUp
  // 1248h532ugm): it rides on the same badge and goes by itself once paid.
  const renderCreditBadge = (creditStatus, condition) => {
    if (!creditStatus) return <span className="fe-credit-badge fe-credit-default">-</span>;
    const klass = CREDIT_BADGE_CLASS[creditStatus] || 'fe-credit-badge fe-credit-default';
    const label = creditStatus === 'n_a' ? 'No Add. Credit' : prettyStage(creditStatus);
    const words = condition?.text ? `${label}, ${condition.text}` : label;
    return (
      <span
        className={klass}
        title={condition?.text ? 'Credit approved on condition that the named invoices are paid first. Nothing is blocked.' : undefined}
      >
        {words}
      </span>
    );
  };

  // Pricing basis cell. Shows which way round the two sides sit, so a hedging
  // trader reads "sales floating, purchase fixed" off the row rather than
  // opening the enquiry to find out.
  //
  // The two values are what the SERVER says the hedging rule reads, printed
  // as they arrive. Nothing is re-decided here: on a multi-product enquiry the
  // product lines decide and the enquiry's own header columns do not, so a cell
  // that read the raw columns would show two agreeing pills beside a "needs
  // hedging" badge and look broken. "Mixed" is a real answer -- product lines
  // that disagree with each other.
  //
  // Both values can be absent: a customer trader is never sent the purchase
  // side (the server strips it), so their rows show only what is theirs.
  const renderPricingCell = (row) => {
    const clean = (value) => {
      const v = (value || '').trim();
      return v && v !== 'Not set' ? v : null;
    };
    const sales = clean(row.pricing_basis_sales);
    const purchase = clean(row.pricing_basis_purchase);
    if (!sales && !purchase) return '-';

    const pill = (side, value) => (
      <span
        className={`fe-pricing-pill fe-pricing-pill--${value.toLowerCase()}`}
        title={`${side === 'S' ? 'Sales' : 'Purchase'} pricing is ${value.toLowerCase()}`}
      >
        {side}: {value}
      </span>
    );

    return (
      <span className="fe-pricing-cell">
        {sales && pill('S', sales)}
        {purchase && pill('P', purchase)}
        {row.requires_hedging === true && (
          <span
            className="fe-hedging-badge"
            title="Sales and purchase pricing disagree - this enquiry carries open price risk"
          >
            NEEDS HEDGING
          </span>
        )}
      </span>
    );
  };

  const renderCreditRequestIcon = (status) => {
    if (!status) return null;
    if (status === 'pending') {
      return (
        <FontAwesomeIcon
          icon={faClock}
          className="fe-credit-request-icon fe-credit-request-icon--pending"
          title="Credit increase request pending approval"
        />
      );
    }
    if (status === 'rejected') {
      return (
        <FontAwesomeIcon
          icon={faCircleXmark}
          className="fe-credit-request-icon fe-credit-request-icon--rejected"
          title="Credit increase request was rejected — open the enquiry to see the reviewer's note"
        />
      );
    }
    if (status === 'approved') {
      return (
        <FontAwesomeIcon
          icon={faCircleCheck}
          className="fe-credit-request-icon fe-credit-request-icon--approved"
          title="Credit increase request was approved"
        />
      );
    }
    return null;
  };

  return (
    <section className="deals-section fe-list-section" data-tour="tradeops-fuel-enquiries-section">
      {/* Action row — copies the Recaps tab's button-group / group-button /
          .grouping-controls structure verbatim (TradeOperationsLayout.js
          line ~2566-2589) so the visuals + placement are identical: the
          left side carries the orange "New X" actions and the right side
          carries the HMS-blue "Enquiry Actions" toggle that swaps to the
          three-button grouping-controls cluster. */}
      <div className="button-group">
        <div className="button-group-left">
          <button onClick={handleNewEnquiry} className="action-button" data-tour="tradeops-new-fuel-enquiry">
            New Enquiry
          </button>
        </div>
        {!groupingMode ? (
          <button
            onClick={enterGroupingMode}
            className="group-button"
          >
            Enquiry Actions
          </button>
        ) : (
          <div className="grouping-controls">
            <button
              onClick={handleGroupSelected}
              className="group-button"
              disabled={groupingBusy}
            >
              Group Selected
            </button>
            <button
              onClick={handleUngroupSelected}
              className="group-button"
              disabled={groupingBusy}
            >
              Ungroup Selected
            </button>
            <button
              onClick={exitGroupingMode}
              className="group-button cancel"
              disabled={groupingBusy}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Filter / search bar. Hidden since the Deals-parity pass and
          un-hidden 2026-09-03: with 69 enquiries on the live system and no
          search box, finding one meant reading the whole table. The state,
          debouncing and query params were retained all along, so this is the
          single edit the old comment anticipated. */}
      {(
        <div className="fe-filter-bar">
          <div className="fe-filter-group">
            <label htmlFor="fe-filter-archived">Show</label>
            <select
              id="fe-filter-archived"
              value={filterState.archived}
              onChange={(e) => handleFilterChange('archived', e.target.value)}
            >
              {ARCHIVE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="fe-filter-group">
            <label htmlFor="fe-filter-stage">Stage</label>
            <select
              id="fe-filter-stage"
              value={filterState.stage}
              onChange={(e) => handleFilterChange('stage', e.target.value)}
            >
              {STAGE_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="fe-filter-group">
            <label htmlFor="fe-filter-credit">Credit Status</label>
            <select
              id="fe-filter-credit"
              value={filterState.credit_status}
              onChange={(e) => handleFilterChange('credit_status', e.target.value)}
            >
              {CREDIT_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="fe-filter-group">
            <label htmlFor="fe-filter-ct">Customer Trader ID</label>
            <input
              id="fe-filter-ct"
              type="text"
              placeholder="Employee ID"
              value={filterState.customer_trader}
              onChange={(e) => handleFilterChange('customer_trader', e.target.value)}
            />
          </div>

          <div className="fe-filter-group">
            <label htmlFor="fe-filter-st">Supplier Trader ID</label>
            <input
              id="fe-filter-st"
              type="text"
              placeholder="Employee ID"
              value={filterState.supplier_trader}
              onChange={(e) => handleFilterChange('supplier_trader', e.target.value)}
            />
          </div>

          {/* Hedging desk. A toggle rather than another dropdown: it answers
              one yes/no question and a trader glancing at the bar can see at
              once whether the list in front of them is the whole book or the
              risk subset. */}
          <div className="fe-filter-group">
            {/* A caption, NOT a <label htmlFor>. A button is a labelable
                element, so an associated label REPLACES its text as the name a
                screen reader reads out -- this control would have announced
                itself as "Risk" and never as what it does. */}
            <span className="fe-filter-caption">Risk</span>
            <button
              type="button"
              className={`fe-hedging-toggle${filterState.requires_hedging ? ' active' : ''}`}
              aria-pressed={filterState.requires_hedging}
              title="Show only enquiries where the sales and purchase pricing disagree - one fixed, one floating"
              onClick={() => handleFilterChange('requires_hedging', !filterState.requires_hedging)}
            >
              Needs hedging
            </button>
          </div>

          <div className="fe-search-group">
            <FontAwesomeIcon icon={faSearch} className="fe-search-icon" />
            <input
              type="text"
              className="fe-search-input"
              placeholder="Search Enquiry ID, Customer or Vessel..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Table chrome — uses the global ``data-table`` / ``table-container``
          / ``empty-state-container`` styles defined in
          ``TradeOperationsLayout.css`` so the visual treatment (paddings,
          radii, badge sizing, empty-state pulse) matches the Deals tab
          exactly. */}
      {!loading && !error && (
        <div
          className="fe-list-count"
          style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', opacity: 0.85 }}
        >
          {count === rows.length
            ? `${count} ${count === 1 ? 'enquiry' : 'enquiries'}`
            : `Showing ${rows.length} of ${count} enquiries`}
          {/* A search spans live AND archived, so the count has to own up to
              it -- otherwise a badged row in a list headed "Live enquiries"
              looks like a bug. Shown whenever archived rows are on screen,
              which covers the Live + archived option too. */}
          {archivedOnScreen > 0 && (
            <span
              className="fe-count-archived-note"
              /* Inline, matching the row badge above -- this file styles its
                 list chrome inline and there is no stylesheet behind
                 ``fe-archived-badge`` to hang a rule on. Colour stated, never
                 inherited. */
              style={{ color: '#475569', fontWeight: 600 }}
            >
              {` · ${archivedOnScreen} archived`}
            </span>
          )}
        </div>
      )}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              {groupingMode && <th className="checkbox-column">Select</th>}
              <th className="hms-deals-grouping-accent-header"></th>
              <th>Enquiry ID</th>
              <th>Customer</th>
              <th>Vessel</th>
              <th>Port</th>
              <th>Pricing</th>
              <th>Stage</th>
              <th>Credit Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td
                  colSpan={groupingMode ? 11 : 10}
                  className="empty-state-cell"
                >
                  <div className="empty-state-container">
                    <div className="empty-state-icon">
                      <FontAwesomeIcon icon={faTint} />
                    </div>
                    <div className="empty-state-text">
                      <h3>Loading Bunker Enquiries</h3>
                      <p>Fetching the latest enquiries — one moment...</p>
                    </div>
                  </div>
                </td>
              </tr>
            )}
            {!loading && error && (
              <tr>
                <td
                  colSpan={groupingMode ? 11 : 10}
                  className="empty-state-cell"
                >
                  <div className="empty-state-container">
                    <div className="empty-state-icon">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                    </div>
                    <div className="empty-state-text">
                      <h3>Couldn't load bunker enquiries</h3>
                      <p>{error}</p>
                      <button
                        type="button"
                        className="fe-retry-btn"
                        onClick={fetchList}
                        style={{ marginTop: '1rem' }}
                      >
                        <FontAwesomeIcon icon={faRedo} /> Retry
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            )}
            {!loading && !error && rows.length === 0 && (
              <tr>
                <td
                  colSpan={groupingMode ? 11 : 10}
                  className="empty-state-cell"
                >
                  <div className="empty-state-container">
                    <div className="empty-state-icon">
                      <FontAwesomeIcon icon={faTint} />
                    </div>
                    <div className="empty-state-text">
                      <h3>No Bunker Enquiries Yet</h3>
                      <p>Enquiries for this period will appear here</p>
                    </div>
                  </div>
                </td>
              </tr>
            )}
            {!loading && !error && displayRows.map(({
              row,
              groupKey,
              groupSize,
              groupId,
              isFirstInGroup,
              isLastInGroup,
              isFirstInTable,
              isLastInTable,
            }) => {
              const isGrouped = groupSize > 1;
              const isSelected = selectedIds.includes(row.id);
              const rowClass = [
                isSelected ? 'selected' : '',
                isGrouped ? 'grouped-row' : '',
                isGrouped && isFirstInGroup ? 'grouped-first' : '',
                isGrouped && isLastInGroup ? 'grouped-last' : '',
                isFirstInTable ? 'table-first' : '',
                isLastInTable ? 'table-last' : '',
                !groupingMode ? 'clickable-row' : '',
              ].filter(Boolean).join(' ');
              const stage = row.stage;
              const isTerminalStage = stage === 'lost' || stage === 'recapped';
              const dropdownKey = `enquiry-${row.id}`;
              const isDropdownOpen = openDropdownId === row.id;
              return (
                <tr
                  key={row.id}
                  className={rowClass}
                  onClick={(e) => {
                    if (e.target.closest('.checkbox-column')) return;
                    if (e.target.closest('.action-dropdown')) return;
                    if (groupingMode) return;
                    handleRowClick(row);
                  }}
                >
                  {groupingMode && (
                    <td
                      className="checkbox-column"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelected(row.id);
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                      />
                    </td>
                  )}
                  <td className="hms-deals-grouping-accent-cell">
                    {isGrouped && isFirstInGroup && (
                      <div
                        className="hms-deals-grouping-accent-bar"
                        style={{
                          ...generateGroupColor(groupKey),
                          height: `calc(${groupSize} * 100% + ${(groupSize - 1) * 1}px - 16px)`,
                        }}
                        title={`Enquiry group ${groupId}`}
                      >
                        <span className="hms-deals-grouping-accent-bar-text">
                          {groupId}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="fe-display-id">
                    {row.display_id || '-'}
                    {/* Without this an archived enquiry is indistinguishable
                        from a live one the moment the list includes both. */}
                    {row.is_archived && (
                      <span
                        className="fe-archived-badge"
                        title="This enquiry has been archived"
                        style={{
                          marginLeft: '0.4rem',
                          padding: '0.05rem 0.35rem',
                          borderRadius: '3px',
                          border: '1px solid #94a3b8',
                          background: '#f1f5f9',
                          color: '#475569',
                          fontSize: '0.68rem',
                          fontWeight: 600,
                          letterSpacing: '0.02em',
                          verticalAlign: 'middle',
                        }}
                      >
                        ARCHIVED
                      </span>
                    )}
                  </td>
                  <td>{row.customer_name || '-'}</td>
                  <td>{row.vessel_name || '-'}</td>
                  <td>{row.port_name || '-'}</td>
                  <td>{renderPricingCell(row)}</td>
                  <td>{renderStageBadge(row.stage)}</td>
                  <td>
                    <span className="fe-credit-cell">
                      {renderCreditBadge(row.credit_status, row.credit_condition)}
                      {renderCreditRequestIcon(row.latest_credit_request_status)}
                    </span>
                  </td>
                  <td className="fe-created-cell">{formatDateTime(row.created_at)}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div
                      className={`action-dropdown ${isDropdownOpen ? 'dropdown-open' : ''}`}
                      ref={(el) => { dropdownRefs.current[row.id] = el; }}
                    >
                      <button
                        type="button"
                        className="gear-button"
                        onClick={() => toggleDropdown(row.id)}
                      >
                        Actions
                      </button>
                      {isDropdownOpen && (
                        <div className="dropdown-menu">
                          <button
                            type="button"
                            onClick={() => handleViewEnquiry(row)}
                          >
                            <FontAwesomeIcon icon={faEye} /> View
                          </button>
                          <button
                            type="button"
                            onClick={() => handleViewRelationships(row)}
                          >
                            <FontAwesomeIcon icon={faProjectDiagram} /> View Relationships
                          </button>
                          {/* Customer Confirmation — only clickable once the
                              Sales Recap exists (``row.trade_recap``), per HMS.
                              Disabled-with-tooltip otherwise, matching Cargo. */}
                          <button
                            type="button"
                            disabled={!row.trade_recap || confirmation.loadingId === row.id}
                            title={row.trade_recap
                              ? 'Generate the Customer Confirmation PDF.'
                              : 'Available once the recap is created for this enquiry.'}
                            onClick={() => handleCustomerConfirmation(row)}
                          >
                            <FontAwesomeIcon
                              icon={confirmation.loadingId === row.id ? faClock : faFileContract}
                            />{' '}
                            {confirmation.loadingId === row.id
                              ? 'Generating…'
                              : 'Customer Confirmation'}
                          </button>
                          {!isTerminalStage && (
                            <>
                              <hr />
                              <button
                                type="button"
                                className="danger"
                                onClick={() => handleOpenMarkLost(row)}
                              >
                                <FontAwesomeIcon icon={faTimesCircle} /> Mark as Lost
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>


      {/* Mark-as-Lost confirmation. Reuses ``StageAdvanceModal``'s
          ``action="lost"`` mode (requires non-empty notes) so the audit
          trail picks up a real reason on every transition. */}
      <StageAdvanceModal
        isOpen={Boolean(lostModalEnquiry)}
        onClose={handleCloseMarkLost}
        onConfirm={handleConfirmMarkLost}
        action="lost"
        fromStage={lostModalEnquiry ? prettyStage(lostModalEnquiry.stage) : ''}
        toStage="Lost"
        requireNotes
        submitting={lostSubmitting}
        error={lostError}
      />

      {/* Relationship graph. Same component used by the Deals/Recaps
          rows; ``documentType="fuel_enquiry"`` routes the fetch to the
          new backend endpoint and the legend's fulfillment/product
          arrows are switched on for this type. */}
      <InvoiceRelationshipModal
        isOpen={showRelationshipModal}
        onClose={() => {
          setShowRelationshipModal(false);
          setRelationshipEnquiry(null);
        }}
        document={relationshipEnquiry}
        documentType="fuel_enquiry"
      />

      {/* Customer Confirmation PDF preview, launched from the row Actions menu. */}
      <PdfPreviewModal
        isOpen={confirmation.open}
        pdfUrl={confirmation.url}
        fileName={confirmation.fileName}
        onClose={handleCloseConfirmation}
        onDownload={handleDownloadConfirmation}
      />
    </section>
  );
};

export default FuelEnquiryList;
